import { type CSSProperties, type ReactNode } from 'react'
import styles from './MobileEditorSheet.module.css'

export type MobileEditorSheetTab<T extends string> = {
  id: T
  label: string
}

type MobileEditorSheetProps<T extends string> = {
  activeTab: T
  actions: ReactNode
  ariaLabel: string
  children: ReactNode
  closeButton: ReactNode
  kicker: string
  onTabChange: (tab: T) => void
  subtitle?: ReactNode
  tabs: Array<MobileEditorSheetTab<T>>
  tabsPlacement?: 'header' | 'belowHeader'
  title: ReactNode
}

const tabClassName = (active: boolean): string =>
  [styles.tab, active ? styles.tabActive : ''].filter(Boolean).join(' ')

export const mobileEditorSheetClassName = (className: keyof typeof styles): string =>
  styles[className] ?? ''

export const MobileEditorSheet = <T extends string>({
  activeTab,
  actions,
  ariaLabel,
  children,
  closeButton,
  kicker,
  onTabChange,
  subtitle,
  tabs,
  tabsPlacement = 'header',
  title
}: MobileEditorSheetProps<T>) => (
  <section
    aria-label={ariaLabel}
    className={[styles.sheet, tabsPlacement === 'belowHeader' ? styles.tabsBelowHeader : ''].filter(Boolean).join(' ')}
    onMouseDown={(event) => event.stopPropagation()}
  >
    <header className={styles.header}>
      <div>
        <span className={styles.kicker}>{kicker}</span>
        <h2>{title}</h2>
        {subtitle ? <p>{subtitle}</p> : null}
      </div>
      {closeButton}
      {tabsPlacement === 'header' && (
        <MobileEditorTabs
          activeTab={activeTab}
          ariaLabel={ariaLabel}
          onTabChange={onTabChange}
          tabs={tabs}
        />
      )}
    </header>

    {tabsPlacement === 'belowHeader' && (
      <MobileEditorTabs
        activeTab={activeTab}
        ariaLabel={ariaLabel}
        onTabChange={onTabChange}
        tabs={tabs}
      />
    )}

    <div className={styles.body}>
      {children}
    </div>

    <footer className={styles.actions}>
      {actions}
    </footer>
  </section>
)

type MobileEditorTabsProps<T extends string> = {
  activeTab: T
  ariaLabel: string
  onTabChange: (tab: T) => void
  tabs: Array<MobileEditorSheetTab<T>>
}

const MobileEditorTabs = <T extends string>({
  activeTab,
  ariaLabel,
  onTabChange,
  tabs
}: MobileEditorTabsProps<T>) => (
  <div
    className={styles.tabs}
    role="tablist"
    aria-label={`${ariaLabel} sections`}
    style={{ '--mobile-editor-tab-count': tabs.length } as CSSProperties}
  >
    {tabs.map((tab) => (
      <button
        aria-selected={activeTab === tab.id}
        className={tabClassName(activeTab === tab.id)}
        key={tab.id}
        onClick={() => onTabChange(tab.id)}
        role="tab"
        type="button"
      >
        {tab.label}
      </button>
    ))}
  </div>
)
